/**
 * SecondaryPane — an independent pane group with its own tab bar.
 *
 * The pane owns its chrome: a tab strip and a close-pane button. Its tabs are
 * rendered by `TabSurfaces`, the same component the primary pane uses, so the
 * two panes cannot drift apart — which they had, repeatedly, while each held
 * its own copy of the rendering.
 */
import React from 'react';

import { Icons } from '@shared/components/ui';

import { TabBar } from './TabBar';
import { TabSurfaces } from './TabSurfaces';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';

export const SecondaryPane: React.FC = () => {
  const closeSidePane = useWorkspaceStore(s => s.closeSidePane);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Tab bar with close-pane button. The row carries the strip's own surface: the button sits beside the
          strip, not in it, and without this the frame would stop at the last
          tab and leave the button in a notch of page colour. */}
      <div
        data-testid="secondary-pane-tabrow"
        className="bg-sidebar flex shrink-0 items-center"
      >
        <div className="min-w-0 flex-1">
          <TabBar group="secondary" />
        </div>
        <button
          type="button"
          onClick={closeSidePane}
          className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent mx-1 shrink-0 rounded-md p-1 transition-colors"
          aria-label="Close side pane"
          title="Close side pane"
        >
          <Icons.X size="xs" />
        </button>
      </div>

      {/* Content — every tab in this group, one visible. */}
      <TabSurfaces group="secondary" />
    </div>
  );
};
